/*
  Raaga home page behaviour (raaga/index.html → ui.js).
  Scroll reveal, snap carousels with real controls, the hero banner, the lookbook
  films, the size tray on a product tile and the add that flies into the bag.
  Everything binds per section so the theme editor can load one in isolation.
*/
(function () {
  'use strict';

  var reduce = function () {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /* ── scroll reveal ─────────────────────────────────────────────
     The observer handles the pretty case; a cheap rAF sweep guarantees that
     anything actually in view is visible however the viewport got there — a
     restored scroll position or a back navigation can otherwise strand it. */
  var revIO = null;
  var sweeping = false;

  function sweepReveal() {
    if (sweeping) return;
    sweeping = true;
    requestAnimationFrame(function () {
      sweeping = false;
      var h = window.innerHeight;
      document.querySelectorAll('.rgh-rev:not(.is-in)').forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < h + 140 && r.bottom > -140) el.classList.add('is-in');
      });
    });
  }

  function reveal(root) {
    var scope = root || document;
    if (reduce()) {
      scope.querySelectorAll('.rgh-rev').forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    if (!revIO && typeof IntersectionObserver === 'function') {
      revIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('is-in'); revIO.unobserve(e.target); }
        });
      }, { rootMargin: '140px 0px', threshold: 0 });
    }
    var pending = scope.querySelectorAll('.rgh-rev:not(.is-in)');
    if (revIO) pending.forEach(function (el) { revIO.observe(el); });
    else pending.forEach(function (el) { el.classList.add('is-in'); });
    sweepReveal();
  }

  /* ── carousels ─────────────────────────────────────────────────
     A scroll-snap track with real controls. The arrows are a convenience —
     the track is a native scroller, so touch, trackpad, shift-wheel and the
     arrow keys all work without them. They disable at each end so they never
     lie about what they will do. */
  function carScroll(el, dir) {
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.82), behavior: reduce() ? 'auto' : 'smooth' });
  }

  function carSync(el) {
    var sec = el.closest('[data-rgh-car]');
    var nav = sec && sec.querySelector('.rgh-car__nav');
    if (!nav) return;
    var max = el.scrollWidth - el.clientWidth;
    nav.hidden = max <= 2;
    var back = nav.querySelector('[data-dir="-1"]');
    var fwd = nav.querySelector('[data-dir="1"]');
    if (back) back.disabled = el.scrollLeft <= 2;
    if (fwd) fwd.disabled = el.scrollLeft >= max - 2;
  }

  function carousels(root) {
    (root || document).querySelectorAll('.rgh-shelf').forEach(function (el) {
      if (el.dataset.rghBound) return;
      /* the bound marker must not be data-rgh-car: that is the attribute the
         section is found by, and writing it onto the shelf makes closest()
         return the shelf itself, so the arrows are never found */
      el.dataset.rghBound = '1';

      el.addEventListener('scroll', function () { carSync(el); }, { passive: true });
      /* scrollWidth settles after fonts and images land, so re-check then too */
      if (typeof ResizeObserver === 'function') new ResizeObserver(function () { carSync(el); }).observe(el);
      el.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); carScroll(el, 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); carScroll(el, -1); }
      });

      var sec = el.closest('[data-rgh-car]');
      if (sec) {
        sec.querySelectorAll('.rgh-car__btn[data-dir]').forEach(function (btn) {
          btn.addEventListener('click', function () { carScroll(el, Number(btn.getAttribute('data-dir'))); });
        });
      }
      carSync(el);
    });
  }

  /* ── the hero banner ───────────────────────────────────────────
     Auto-advances, with dots, arrows and a pause control — moving content
     that runs past five seconds has to be stoppable. It holds while a
     pointer is over it, while focus is inside it, while the tab is hidden,
     and never starts at all under prefers-reduced-motion. */
  function bannerInit(box) {
    if (box.dataset.rghBan) return;
    box.dataset.rghBan = '1';

    var track = box.querySelector('.rgh-ban__track');
    var dots = Array.prototype.slice.call(box.querySelectorAll('.rgh-ban__dots button'));
    var pause = box.querySelector('.rgh-ban__pause');
    var n = box.querySelectorAll('.rgh-ban__slide').length;
    if (!track || n < 1) return;

    var state = { i: 0, timer: null, wanted: !reduce(), held: false };
    var ms = Number(box.getAttribute('data-interval')) || 5200;

    function index() { return Math.round(track.scrollLeft / (track.clientWidth || 1)); }

    function paint() {
      dots.forEach(function (b, ix) {
        b.classList.toggle('is-on', ix === state.i);
        b.setAttribute('aria-selected', String(ix === state.i));
      });
      if (!pause) return;
      pause.setAttribute('aria-pressed', String(!state.wanted));
      pause.setAttribute('aria-label', pause.getAttribute(state.wanted ? 'data-label-pause' : 'data-label-play') || '');
      pause.querySelectorAll('[data-icon]').forEach(function (svg) {
        svg.hidden = svg.getAttribute('data-icon') !== (state.wanted ? 'pause' : 'play');
      });
    }

    function to(i, smooth) {
      state.i = ((i % n) + n) % n;
      track.scrollTo({ left: state.i * track.clientWidth, behavior: smooth === false || reduce() ? 'auto' : 'smooth' });
      paint();
    }

    function run() {
      clearInterval(state.timer);
      state.timer = null;
      if (state.wanted && !state.held && !document.hidden && !reduce() && n > 1) {
        /* step from where the track actually is, not from a stored index — a
           dropped or interrupted scroll would otherwise desync the dots */
        state.timer = setInterval(function () { to(index() + 1); }, ms);
      }
      paint();
    }

    function hold(v) { if (state.held === v) return; state.held = v; run(); }

    box.addEventListener('mouseenter', function () { hold(true); });
    box.addEventListener('mouseleave', function () { hold(false); });
    box.addEventListener('focusin', function () { hold(true); });
    box.addEventListener('focusout', function () { if (!box.contains(document.activeElement)) hold(false); });
    track.addEventListener('pointerdown', function () { hold(true); }, { passive: true });
    track.addEventListener('scroll', function () { state.i = index(); paint(); }, { passive: true });
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); to(index() + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); to(index() - 1); }
    });
    document.addEventListener('visibilitychange', run);

    box.querySelectorAll('[data-ban-go]').forEach(function (btn) {
      btn.addEventListener('click', function () { to(index() + Number(btn.getAttribute('data-ban-go'))); });
    });
    dots.forEach(function (b, ix) { b.addEventListener('click', function () { to(ix); }); });
    if (pause) pause.addEventListener('click', function () { state.wanted = !state.wanted; state.held = false; run(); });

    if (typeof IntersectionObserver === 'function') {
      new IntersectionObserver(function (e) { hold(!e[0].isIntersecting); }, { threshold: 0.2 }).observe(box);
    }
    window.addEventListener('resize', function () { to(state.i, false); }, { passive: true });
    run();
  }

  /* ── the films ─────────────────────────────────────────────────
     Each film rolls by itself — muted, looping, inline — from the moment it
     scrolls into view, and pauses the instant it leaves or the tab is
     hidden, so nothing streams off-screen. They stay silent: a storefront
     that makes noise at a stranger is a storefront they leave, so there is
     no sound and no switch to ask for it. Hovering a film raises a pause,
     because moving content has to be stoppable, and a film a visitor stops
     stays stopped — scrolling back to it will not start it again. Under
     prefers-reduced-motion nothing auto-plays at all; the poster holds and
     the button is a plain play with the browser's own controls behind it. */
  function films(root) {
    (root || document).querySelectorAll('.rgh-film[data-video]').forEach(function (fig) {
      if (fig.dataset.rghFilm) return;
      fig.dataset.rghFilm = '1';

      var src = fig.getAttribute('data-video');
      var btn = fig.querySelector('.rgh-film__play');
      var iPlay = fig.querySelector('.rgh-film__i--play');
      var iPause = fig.querySelector('.rgh-film__i--pause');
      var auto = !reduce();
      var v = null;
      var near = false;
      var stopped = false;

      fig.dataset.mode = auto ? 'roll' : 'play';

      /* The button says what pressing it will do. */
      function face(playing) {
        if (iPlay) iPlay.hidden = playing;
        if (iPause) iPause.hidden = !playing;
        if (!btn) return;
        var key = playing ? 'data-label-pause' : 'data-label-play';
        btn.setAttribute('aria-label', btn.getAttribute(key) || (playing ? 'Pause the film' : 'Play the film'));
        fig.classList.toggle('is-held', !playing);
      }

      function mount() {
        if (v) return v;
        v = document.createElement('video');
        var poster = fig.querySelector('img');
        if (poster) v.poster = poster.currentSrc || poster.src;
        v.muted = true;
        v.defaultMuted = true;
        v.loop = true;
        v.playsInline = true;
        v.setAttribute('muted', '');
        v.setAttribute('playsinline', '');
        v.preload = 'metadata';
        v.controls = !auto;
        v.setAttribute('aria-label', fig.getAttribute('data-title') || '');
        v.src = src;
        fig.appendChild(v);
        fig.dataset.on = '1';
        return v;
      }

      function play() {
        var p = mount().play();
        if (p && p.catch) p.catch(function () { if (!auto) v.controls = true; });
        if (auto) face(true);
      }

      if (!auto) {
        if (btn) {
          btn.addEventListener('click', function () {
            if (fig.dataset.on) return;
            play();
          });
        }
        return;
      }

      if (btn) {
        btn.addEventListener('click', function () {
          if (v && !v.paused) {
            stopped = true;
            v.pause();
            face(false);
          } else {
            stopped = false;
            play();
          }
        });
      }

      function run() {
        if (stopped) return;
        if (near && !document.hidden) play();
        else if (v) { v.pause(); face(false); }
      }

      if (typeof IntersectionObserver === 'function') {
        new IntersectionObserver(function (e) {
          near = e[0].isIntersecting;
          run();
        }, { threshold: 0.35 }).observe(fig);
      } else {
        near = true;
        run();
      }
      document.addEventListener('visibilitychange', run);
    });
  }

  /* ── the size tray ─────────────────────────────────────────────
     Press the bag on a tile, the sizes slide up over the photograph; pick
     one and the garment flies into the bag. Touch gets exactly the same
     flow — the button is always visible, so nothing depends on a hover a
     phone cannot perform. */
  function closeTrays(except) {
    document.querySelectorAll('.rgh-tray.is-on').forEach(function (t) {
      if (t === except) return;
      t.classList.remove('is-on');
      var btn = document.querySelector('[aria-controls="' + t.id + '"]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
  }

  function trays(root) {
    (root || document).querySelectorAll('[data-rgh-tray-toggle]').forEach(function (btn) {
      if (btn.dataset.rghBound) return;
      btn.dataset.rghBound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var tray = document.getElementById(btn.getAttribute('aria-controls'));
        if (!tray) return;
        var open = !tray.classList.contains('is-on');
        closeTrays(tray);
        tray.classList.toggle('is-on', open);
        btn.setAttribute('aria-expanded', String(open));
        if (open) {
          var first = tray.querySelector('.rgh-szbtn:not(:disabled)');
          if (first) first.focus();
        }
      });
    });
  }

  /* ── add to bag ────────────────────────────────────────────────
     The same request the theme's own product form makes, so the cart drawer
     and the bag count stay the theme's. */
  function fly(fromEl, imgSrc) {
    if (reduce() || !imgSrc) return;
    var target = document.getElementById('cart-icon-bubble');
    if (!target || !fromEl) return;
    var a = fromEl.getBoundingClientRect();
    var b = target.getBoundingClientRect();
    var ghost = document.createElement('img');
    ghost.src = imgSrc;
    ghost.className = 'rgh-fly';
    ghost.alt = '';
    ghost.style.cssText = 'left:' + a.left + 'px;top:' + a.top + 'px;width:' + a.width + 'px;height:' + a.height + 'px;opacity:.96';
    document.body.appendChild(ghost);
    requestAnimationFrame(function () {
      ghost.style.transition = 'transform .85s cubic-bezier(.4,0,.2,1), opacity .85s ease, width .85s ease, height .85s ease';
      ghost.style.transform =
        'translate(' + (b.left + b.width / 2 - a.left - a.width / 2) + 'px,' +
        (b.top + b.height / 2 - a.top - a.height / 2) + 'px) scale(.12)';
      ghost.style.opacity = '0.2';
    });
    setTimeout(function () { ghost.remove(); }, 900);
  }

  function bumpBag(count) {
    var bubble = document.querySelector('#cart-icon-bubble .rg-cart-count');
    if (bubble) {
      bubble.textContent = count < 100 ? String(count) : '99+';
      bubble.classList.toggle('on', count > 0);
    }
    var btn = document.getElementById('cart-icon-bubble');
    if (!btn) return;
    btn.classList.remove('bump');
    void btn.offsetWidth;
    btn.classList.add('bump');
  }

  function openDrawer(html) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return false;
    if (html) {
      var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
      var live = document.querySelector('#CartDrawer');
      if (parsed && live) live.innerHTML = parsed.innerHTML;
    }
    var inner = drawer.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');
    drawer.classList.remove('is-empty');
    if (typeof drawer.open === 'function') drawer.open(document.getElementById('cart-icon-bubble'));
    return true;
  }

  function addToBag(btn) {
    var id = btn.getAttribute('data-variant');
    if (!id || btn.dataset.busy) return;
    btn.dataset.busy = '1';

    var tile = btn.closest('.rgh-tile');
    var bag = tile && tile.querySelector('.rgh-tile__add');
    var photo = tile && tile.querySelector('.rgh-tile__img--a');
    if (bag) bag.classList.add('is-busy');

    var body = new FormData();
    body.append('id', id);
    body.append('quantity', '1');
    body.append('sections', 'cart-drawer');
    body.append('sections_url', window.location.pathname);

    var url = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) {
          btn.setAttribute('aria-invalid', 'true');
          btn.disabled = true;
          return;
        }
        closeTrays();
        if (photo) fly(photo, photo.currentSrc || photo.src);
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            bumpBag(cart.item_count);
            var sections = data.sections || {};
            setTimeout(function () {
              if (!openDrawer(sections['cart-drawer'])) window.location.href = (window.routes && window.routes.cart_url) || '/cart';
            }, reduce() ? 0 : 620);
          });
      })
      .catch(function () {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      })
      .finally(function () {
        delete btn.dataset.busy;
        if (bag) bag.classList.remove('is-busy');
      });
  }

  function sizeButtons(root) {
    (root || document).querySelectorAll('.rgh-szbtn[data-variant]').forEach(function (btn) {
      if (btn.dataset.rghBound) return;
      btn.dataset.rghBound = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        addToBag(btn);
      });
    });
  }

  function boot(root) {
    reveal(root);
    carousels(root);
    films(root);
    trays(root);
    sizeButtons(root);
    (root || document).querySelectorAll('.rgh-ban').forEach(bannerInit);
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest('.rgh-tray') && !e.target.closest('[data-rgh-tray-toggle]')) closeTrays();
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeTrays(); });

  window.addEventListener('scroll', sweepReveal, { passive: true });
  window.addEventListener('resize', sweepReveal, { passive: true });
  window.addEventListener('pageshow', sweepReveal);
  window.addEventListener('resize', function () {
    document.querySelectorAll('.rgh-shelf').forEach(carSync);
  }, { passive: true });
  window.addEventListener('load', function () {
    document.querySelectorAll('.rgh-shelf').forEach(carSync);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
